import { navigate } from "gatsby";

import create from "zustand";

import { errorAwareApiFetch } from "../../../../../apiBindings";
import { deliveryTimeOptions } from "../../../../../providers/DeliveryTime";
import {
    storageLocal as storage,
    storageSession as session,
} from "../../../../../utils/StorageHandler";
import createFormErrorSlices from "../../../../../utils/store/formErrors";
import resolveWebView from "../../../../../utils/webViewRedirect";
import iCheckoutData from "../../interfaces/iCheckoutData";
import iNotify from "../../interfaces/iNotify";

export enum DeliveryTime {
    standard = "standard",
    short = "short",
    express48 = "express48",
    express24 = "express24",
}

export enum DeliveryTimeType {
    standard = "standard",
    time = "time",
    date = "date",
}

export enum DeliveryTimeFrame {
    none = "",
    "7to9" = "7to9",
    "7to12" = "7to12",
    "12to16" = "12to16",
    "16to18" = "16to18",
}

interface iHeatingOilDeliveryDetails {
    deliveryTime: DeliveryTime;
    deliveryTimeType: DeliveryTimeType;
    deliveryTimeFrame: DeliveryTimeFrame;
    deliveryTimeWishDate: string;
    antiFreeze: any;
    hose: number;
    orderComment: string;
    climateNeutral: boolean;
    promotionCode: string;
    setDeliveryTime: (deliveryTime: DeliveryTime) => void;
    setDeliveryTimeType: (deliveryTimeType: DeliveryTimeType) => void;
    setDeliveryTimeFrame: (deliveryTimeFrame: DeliveryTimeFrame) => void;
    setDeliveryTimeWishDate: (deliveryTimeWishDate: string) => void;
    setClimateNeutral: (climateNeutral: boolean) => void;
    setAntiFreeze: (antiFreeze: any) => void;
    setHose: (hose: number) => void;
    setOrderComment: (orderComment: string) => void;
    storeClimateNeutralToStorage: () => void;
    storeOrderCommentToStorage: () => void;
    storePromotionCodeToStorage: () => void;
    storeHoseToStorage: () => void;
    storeDeliveryTimeToStorage: () => void;
    storeDeliveryTimeTypeToStorage: () => void;
    storeAntiFreezeToStorage: () => void;
    storeDeliveryTimeFrameToStorage: () => void;
    storeDeliveryTimeWishDateToStorage: () => void;
    storeDeliveryDetailsToServer: (
        checkoutData: iCheckoutData,
        notify: iNotify,
        calculateOnly: boolean,
        isOverview: boolean,
        onSuccess: () => void | undefined
    ) => Promise<iCheckoutData>;
    errors: string[];
    setErrors: (errors: string[]) => void;
    addError: (error: string) => void;
    clearError: (error: string) => void;
    hasErrorAt: (error: string) => boolean;
}

const useStore = create<iHeatingOilDeliveryDetails>((set, get) => ({
    ...createFormErrorSlices(set, get),
    deliveryTime: storage.get(
        "hel:delivery-time",
        storage.get("hel:delivery-time", "standard")
    ),
    deliveryTimeType: storage.get("hel:delivery-time-type", "standard"),
    deliveryTimeFrame: storage.get(
        "hel:delivery-time-frame",
        DeliveryTimeFrame.none
    ),
    deliveryTimeWishDate: storage.get("hel:delivery-time-wish-date", ""),
    antiFreeze: storage.getInt("hel:anti-freeze", 0),
    hose: storage.get("hel:hose", 1),
    orderComment: storage.get("hel:order-comment", ""),
    climateNeutral: storage.getBoolean("hel:climate-neutral"),
    promotionCode: storage.get("hel:promotion-code", ""),

    setDeliveryTime: (deliveryTime: DeliveryTime) => {
        set({ deliveryTime: deliveryTime });
    },

    setDeliveryTimeType: (deliveryTimeType: DeliveryTimeType) => {
        set({ deliveryTimeType: deliveryTimeType });
    },

    setDeliveryTimeFrame: (deliveryTimeFrame: DeliveryTimeFrame) => {
        set({ deliveryTimeFrame: deliveryTimeFrame });
    },

    setDeliveryTimeWishDate: (deliveryTimeWishDate: string) => {
        set({ deliveryTimeWishDate: deliveryTimeWishDate });
    },

    setClimateNeutral: (climateNeutral: boolean) => {
        set({ climateNeutral: climateNeutral });
    },

    setAntiFreeze: (antiFreeze: any) => {
        set({ antiFreeze: antiFreeze });
    },

    setHose: (hose: number) => {
        set({ hose: hose });
    },

    setOrderComment: (orderComment: string) => {
        set({ orderComment: orderComment });
    },

    storeClimateNeutralToStorage: () => {
        storage.set("hel:climate-neutral", get().climateNeutral);
    },

    storeOrderCommentToStorage: () => {
        storage.set("hel:order-comment", get().orderComment);
    },

    storePromotionCodeToStorage: () => {
        storage.set("hel:promotion-code", get().promotionCode);
    },

    storeHoseToStorage: () => {
        storage.set("hel:hose", get().hose);
    },

    storeDeliveryTimeToStorage: () => {
        storage.set("hel:delivery-time", get().deliveryTime);
    },

    storeDeliveryTimeTypeToStorage: () => {
        storage.set("hel:delivery-time-type", get().deliveryTimeType);
    },

    storeAntiFreezeToStorage: () => {
        storage.set("hel:anti-freeze", get().antiFreeze);
    },

    storeDeliveryTimeFrameToStorage: () => {
        if (get().deliveryTimeType !== DeliveryTimeType.time) {
            storage.set("hel:delivery-time-frame", "");
            set({ deliveryTimeFrame: DeliveryTimeFrame.none });
        } else {
            storage.set("hel:delivery-time-frame", get().deliveryTimeFrame);
        }
    },

    storeDeliveryTimeWishDateToStorage: () => {
        if (get().deliveryTimeType !== deliveryTimeOptions.date) {
            storage.set("hel:delivery-time-wish-date", "");
            set({ deliveryTimeWishDate: "" });
        } else {
            storage.set(
                "hel:delivery-time-wish-date",
                get().deliveryTimeWishDate
            );
        }
    },

    lieferdetailsStepIsValid: () => {
        return true;
    },

    storeDeliveryDetailsToServer: async (
        checkoutData,
        notify,
        calculateOnly = false,
        isOverview = false,
        onSuccess
    ): Promise<iCheckoutData> => {
        set({ errors: [] });
        const deliverDetails = get();
        const checkoutDataResponse = { ...checkoutData };
        const data = {
            hash: checkoutData.user_hash,
            climateNeutral: deliverDetails.climateNeutral,
            antiFreeze: deliverDetails.antiFreeze,
            hose: deliverDetails.hose,
            deliveryTime: deliverDetails.deliveryTime,
            deliveryTimeFrame:
                deliverDetails.deliveryTimeType !== DeliveryTimeType.time ||
                deliverDetails.deliveryTimeFrame === null
                    ? ""
                    : deliverDetails.deliveryTimeFrame,
            deliveryTimeWishDate:
                deliverDetails.deliveryTimeType !== DeliveryTimeType.date
                    ? ""
                    : deliverDetails.deliveryTimeWishDate,
            comment: deliverDetails.orderComment,
            promotionCode: deliverDetails.promotionCode,
            calculateOnly: calculateOnly,
        };

        return errorAwareApiFetch<{ data: any }>(
            `/heatingoil/checkout/deliveryDetails`,
            {
                method: "POST",
                body: JSON.stringify(data),
            }
        )
            .then(({ data }) => {
                if (!calculateOnly) {
                    session.setJson("hel:checkout", data);
                }
                if (onSuccess) {
                    onSuccess();
                }

                return data;
            })
            .catch(({ data }) => {
                const errors = Object.keys(data.errors);
                set({ errors: errors });
                notify("Fehlgeschlagen", data.message);
                if (!isOverview) {
                    navigate(
                        resolveWebView("/heizoel/bestellung/lieferadresse/")
                    );
                }

                return checkoutDataResponse;
            });
    },

    setOrderCommentStorageToState: () => {
        set({ orderComment: storage.get("hel:order-comment", "") });
    },

    setPromotionCodeStorageToState: () => {
        set({ promotionCode: storage.get("hel:promotion-code", "") });
    },

    setClimateNeutralStorageToState: () => {
        set({ climateNeutral: storage.getBoolean("hel:climate-neutral") });
    },

    setHoseStorageToState: () => {
        set({ hose: storage.get("hel:hose", 1) });
    },

    setDeliveryTimeStorageToState: () => {
        set({ deliveryTime: storage.get("hel:delivery-time", "standard") });
    },

    setDeliveryTimeTypeStorageToState: () => {
        set({
            deliveryTimeType: storage.get("hel:delivery-time-type", "standard"),
        });
    },

    setAntiFreezeStorageToState: () => {
        set({ antiFreeze: storage.get("hel:anti-freeze", 0) });
    },

    setDeliveryTimeFrameToState: () => {
        set({ deliveryTimeFrame: storage.get("hel:delivery-time-frame", "") });
    },

    setDeliveryTimeWishDateToState: () => {
        set({
            deliveryTimeWishDate: storage.get(
                "hel:delivery-time-wish-date",
                ""
            ),
        });
    },

    clearDeliveryDetails: () => {
        set({
            deliveryTime: DeliveryTime.standard,
            deliveryTimeType: DeliveryTimeType.standard,
            deliveryTimeFrame: DeliveryTimeFrame.none,
            deliveryTimeWishDate: "",
            hose: 1,
            antiFreeze: 0,
        });
        storage.remove("hel:anti-freeze");
        storage.remove("hel:hose");
        storage.remove("hel:delivery-time-wish-date");
        storage.remove("hel:delivery-time-type");
        storage.remove("hel:delivery-time-frame");
        storage.remove("hel:delivery-time");
    },
}));

export default useStore;
